import { useEffect, useState } from 'react'

import { useTranslation } from 'react-i18next'
import { useQuery } from 'react-query'
import { useNavigate } from 'react-router-dom'

import * as Icons from 'assets/svg/sprite'
import { IconPlusInBox } from 'assets/svg/sprite'
import { ActionCard } from 'components-ref/molecules/ActionCard/ActionCard'
import { useModalContext } from 'context/modalContext'
import LayoutHeader from 'features/LayoutHeader/LayoutHeader'
import WithHeaderSection from 'layouts/WithHeaderSection/WithHeaderSection'
import { getFansGroups } from 'services/endpoints/fanGroup'

import { AddFansGroupModal } from './AddFansGroupModal'
import { DeleteFansGroupModal } from './DeleteFansGroupModal'
import { EditFansGroupModal } from './EditFansGroupModal'

type GroupSortType =
  | ''
  | 'date_added'
  | 'number_of_fans'
  | 'name'

type GroupSortOrder = '' | 'asc' | 'desc'

export interface FansGroup {
  id: string
  name: string
  slug: string
  count: number
  default: number
  primary?: number
  selected?: boolean
  created_at?: string
}

interface ActiveGroup {
  id: string
  name: string
}

export const FansHome = () => {
  const [searchTerm, setSearchTerm] = useState('')
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('')

  const [sortOptions, setSortOptions] = useState<{
    type: GroupSortType
    asc_or_desc: GroupSortOrder
  }>({
    type: 'date_added',
    asc_or_desc: 'asc'
  })

  const [drawerOpen, setDrawerOpen] = useState(false)

  const [activeGroup, setActiveGroup] = useState<ActiveGroup>({
    id: '',
    name: ''
  })

  const [groupNames, setGroupNames] = useState<string[]>([])

  const modalData = useModalContext()
  const navigate = useNavigate()
  const { t } = useTranslation()

  /*
   * Search debounce.
   *
   * Filtering/searching is performed by the backend.
   */
  useEffect(() => {
    const timeout = window.setTimeout(() => {
      setDebouncedSearchTerm(searchTerm.trim())
    }, 300)

    return () => {
      window.clearTimeout(timeout)
    }
  }, [searchTerm])

  /*
   * This is intentionally a separate query variation from:
   *
   * ['fans-groups']
   *
   * Fans.tsx owns the canonical unfiltered query so it can resolve
   * a URL slug to the corresponding group ULID.
   *
   * FansHome owns the searchable/sortable list.
   */
  const {
    data: groups = [],
    isLoading,
    isFetching
  } = useQuery<FansGroup[]>(
    [
      'fans-groups',
      'list',
      debouncedSearchTerm,
      sortOptions.type,
      sortOptions.asc_or_desc
    ],
    async () => {
      return await getFansGroups({
        search: debouncedSearchTerm || undefined,
        sort_by: sortOptions.type || undefined,
        sort_order: sortOptions.asc_or_desc || undefined
      })
    },
    {
      keepPreviousData: true,
      staleTime: 30_000,

      onSuccess: response => {
        setGroupNames(response.map(group => group.name))
      }
    }
  )

  const sortListsGroups = [
    {
      value: 'date_added',
      name: t('dateAdded')
    },
    {
      value: 'number_of_fans',
      name: t('numberOfFans')
    },
    {
      value: 'name',
      name: t('name')
    }
  ]

  const sortElementsOrder = [
    {
      value: 'asc',
      name: t('ascending')
    },
    {
      value: 'desc',
      name: t('descending')
    }
  ]

  /*
   * Prevent body scrolling while the group action drawer is open.
   */
  useEffect(() => {
    if (drawerOpen) {
      document.body.classList.add(
        'fans-groups-drawer-scrollblock'
      )
    } else {
      document.body.classList.remove(
        'fans-groups-drawer-scrollblock'
      )
    }

    return () => {
      document.body.classList.remove(
        'fans-groups-drawer-scrollblock'
      )
    }
  }, [drawerOpen])

  const closeDrawer = () => {
    setDrawerOpen(false)

    setActiveGroup({
      id: '',
      name: ''
    })
  }

  return (
    <WithHeaderSection
      customClass='fans-home'
      headerSection={
        <LayoutHeader
          type='search-with-buttons'
          searchValue={searchTerm}
          searchFn={(term: string) => {
            setSearchTerm(term)
          }}
          clearFn={() => {
            setSearchTerm('')
            setDebouncedSearchTerm('')
          }}
          additionalProps={{
            placeholder: t('searchGroups')
          }}
          sortingProps={{
            selectedSort: sortOptions.type,
            selectedOrder: sortOptions.asc_or_desc
          }}
          buttons={[
            {
              type: 'sort',
              elements: {
                first_section: sortListsGroups,
                second_section: sortElementsOrder
              },
              defaultOrderBySort: {
                date_added: 'desc',
                number_of_fans: 'desc',
                name: 'asc'
              }
            },
            {
              type: 'custom',
              icon: <Icons.IconPlus />,
              color: 'white',
              action: () => {
                modalData.addModal(
                    t('createNewGroup'),
                    <AddFansGroupModal
                        groupNames={groupNames}
                    />,
                    false,
                    false,
                    '',
                    '',
                    true // keyboardAdjust
                )
              },
              customClass: 'fans-group-add-button'
            }
          ]}
          applyFn={(
            value: string,
            ascOrDesc: string
          ) => {
            setSortOptions({
              type: value as GroupSortType,
              asc_or_desc: ascOrDesc as GroupSortOrder
            })
          }}
        />
      }
    >
      {isLoading && (
        <div className='fans-home-loader-container'>
          <div className='loader' />
        </div>
      )}

      {isFetching && !isLoading && (
        <div className='fans-home-search-loader'>
          <div className='loader' />
        </div>
      )}

      {!isLoading &&
        groups.map(group => (
          <ActionCard
            key={group.id}
            icon={<Icons.IconTwoPeople />}
            iconSize='small'
            text={t(group.name)}
            subtext={
              group.count > 0
                ? `${group.count} ${t('fans')}`
                : t('empty')
            }
            hasArrow
            absFix
            clickFn={() => {
              /*
               * URL remains slug-based.
               *
               * Fans.tsx resolves this slug against its canonical
               * groups query and passes the full group into
               * FansCustomGroup.
               *
               * No IDs need to be placed in navigation state.
               */
              navigate(`/fans/${group.slug}`)
            }}
            customHtml={
              group.default === 0 ? (
                <div
                  onClick={event => {
                    event.stopPropagation()

                    setDrawerOpen(true)

                    setActiveGroup({
                      id: group.id,
                      name: group.name
                    })
                  }}
                >
                  <div
                    className={`fans-groups-drawer-button ${
                      drawerOpen &&
                      activeGroup.id === group.id
                        ? 'fans-groups-drawer-button-open'
                        : ''
                    }`}
                  >
                    <Icons.IconThreeDots color='#767676' />
                  </div>
                </div>
              ) : null
            }
          />
        ))}

      {!isLoading && groups.length === 0 && (
        <div className='fans-groups-empty-results'>
          {t('noResults')}
        </div>
      )}

      {!isLoading && (
        <ActionCard
          icon={
            <IconPlusInBox
              width='22'
              height='22'
            />
          }
          text={t('addNewGroup')}
          iconSize='small'
          addNewText
          clickFn={() => {
            modalData.addModal(
              t('createNewGroup'),
              <AddFansGroupModal
                groupNames={groupNames}
              />
            )
          }}
        />
      )}

      <div
        className={`fans-groups-drawer-overlay ${
          drawerOpen
            ? 'fans-groups-drawer-overlay-open'
            : ''
        }`}
      >
        <div
          className='fans-groups-drawer-overlay-background'
          onClick={closeDrawer}
        />

        <div className='fans-groups-drawer'>
          <div
            className='fans-groups-drawer-option'
            onClick={() => {
              modalData.addModal(
                  t('editGroupName'),
                  <EditFansGroupModal
                      activeGroup={activeGroup}
                      closeDrawer={closeDrawer}
                      groupNames={groupNames}
                  />,
                  false,
                  false,
                  '',
                  '',
                  true // keyboardAdjust
              )
            }}
          >
            <Icons.IconEdit />

            {t('renameGroup')}
          </div>

          <div
            className='media-categories-drawer-option'
            onClick={() => {
              modalData.addModal(
                t('deleteGroup'),
                <DeleteFansGroupModal
                  activeGroup={activeGroup}
                  closeDrawer={closeDrawer}
                />
              )
            }}
          >
            <Icons.IconTrashcan
              width='19'
              height='19'
            />

            {t('deleteGroup')}
          </div>
        </div>
      </div>
    </WithHeaderSection>
  )
}